import { Wallet, ethers, formatUnits } from "ethers";
import { RESCUE_RECIPIENT_KEY_STORAGE_KEY } from "~~/shared/utils/cryptography.utils";

export const useWeb3 = () => {
  const generateWallet = () => {
    const newWallet = ethers.Wallet.createRandom();
    setPrivateKey(newWallet.privateKey);
    return newWallet;
  };

  const setPrivateKey = async (privateKey: string): Promise<void> => {
    useCookie(`wid-${useUserStore().id}`, {
      maxAge: 60 * 60,
      expires: new Date(Date.now() + 60 * 60 * 1000),
    }).value = await obfuscate(privateKey);
  };

  const getAddress = async (): Promise<string> => {
    const wallet = await getWallet();
    if (!wallet) throw new Error("wallet not found.");
    return wallet.address;
  };

  const getPrivateKey = async (): Promise<string> => {
    const userStore = useUserStore();
    if (userStore.keys?.privateKey?.value)
      return userStore.keys.privateKey.value;
    const widCookie = useCookie(`wid-${useUserStore().id}`);
    if (!widCookie.value) throw new Error("wid not found.");
    const key = await deobfuscate(widCookie.value);
    if (!key) throw new Error("key not found.");
    return key;
  };

  const getWallet = async (): Promise<Wallet | null> => {
    try {
      const privateKey = await getPrivateKey();
      if (!privateKey) throw new Error("privateKey not found.");
      const wallet = new Wallet(privateKey);
      return wallet;
    } catch (error) {
      console.log("getWallet error", error);
      return null;
    }
  };

  const confirmPrivateKey = (
    privateKey: string,
    publicAddress: string
  ): boolean => {
    try {
      const wallet = new ethers.Wallet(privateKey);
      return wallet.address.toLowerCase() === publicAddress.toLowerCase();
    } catch (error) {
      console.error("Error confirming private key:", error);
      return false;
    }
  };

  const web3Sign = async (operationHash: string): Promise<string> => {
    const wallet = await getWallet();
    if (!wallet) throw new Error("wallet not found.");
    return wallet.signMessage(ethers.getBytes(operationHash));
  };

  const clearRescueRecipientKey = () => {
    if (import.meta.client)
      sessionStorage.removeItem(RESCUE_RECIPIENT_KEY_STORAGE_KEY);
  };

  const getPKfromRescueKey = async (id: string, key: string) => {
    try {
      const rescue = useCookie<RescueCookie>(`gfalIdRescue-${id}`);
      if (!rescue.value) throw new Error("Missing rescue");
      if (!rescue.value.web3AuthKey) throw new Error("Missing recovery key");

      let rescuePrivateKey: string;
      if (rescue.value.recipientPublicKey) {
        if (!import.meta.client)
          throw new Error("Rescue recovery must run in the browser");
        const rawKeyPair = sessionStorage.getItem(
          RESCUE_RECIPIENT_KEY_STORAGE_KEY
        );
        if (!rawKeyPair) throw new Error("Missing rescue recipient key");
        const keyPair = JSON.parse(rawKeyPair) as {
          privateKey?: string;
          publicKey?: string;
        };
        if (!keyPair.privateKey || !keyPair.publicKey)
          throw new Error("Invalid rescue recipient key");
        if (keyPair.publicKey !== rescue.value.recipientPublicKey)
          throw new Error("Rescue recipient key mismatch");
        rescuePrivateKey = await decryptAsymmetric(
          rescue.value.web3AuthKey,
          keyPair.privateKey
        );
      } else {
        // Keep old rescue cookies usable while all new attempts use the browser envelope.
        rescuePrivateKey = await deobfuscate(rescue.value.web3AuthKey);
      }

      const productionPrivateKey = await decryptAsymmetric(
        key,
        rescuePrivateKey
      );
      await setPrivateKey(productionPrivateKey);
    } finally {
      clearRescueRecipientKey();
    }
  };

  const confirmPKexists = async () => {
    const pk = await getPrivateKey();
    if (!pk) throw new Error("Failed to get private key");
    return true;
  };

  const convertTokenBalance = (
    balance: string,
    decimals: number = 18
  ): number => {
    try {
      const formattedBalance = formatUnits(balance, decimals);
      return Number(formattedBalance);
    } catch (error) {
      throw new Error("Error converting token balance");
    }
  };

  const getUserBalance = async () => {
    const userStore = useUserStore();
    const { contractAddress, bscScanChainId, bscScanApiKey } =
      useRuntimeConfig().public;
    if (!userStore.accountAddress) throw new Error("User address not found");
    const response = await $fetch<{
      status: string;
      message: string;
      result: string;
    }>("https://api.etherscan.io/v2/api", {
      query: {
        chainId: bscScanChainId,
        module: "account",
        action: "tokenbalance",
        contractaddress: contractAddress,
        address: userStore.accountAddress,
        tag: "latest",
        apikey: bscScanApiKey,
      },
    });
    if (!response || !response.result || response.status !== "1")
      throw new Error("Failed to fetch balance");
    const GFALBalance = convertTokenBalance(response.result, 18);
    const GFALBalanceInUSD = useGFALExStore().convertToUSD(
      GFALBalance.toString()
    );
    return { GFAL: GFALBalance, USD: GFALBalanceInUSD };
  };

  return {
    generateWallet,
    setPrivateKey,
    getPrivateKey,
    getAddress,
    getWallet,
    confirmPrivateKey,
    web3Sign,
    getPKfromRescueKey,
    clearRescueRecipientKey,
    confirmPKexists,
    fetchBalance: getUserBalance,
  };
};
